import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Navigate } from 'react-router-dom';
import useLicenseAccess from '@/hooks/useLicenseAccess';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Globe, Check, X, ExternalLink, Loader2, Image as ImageIcon, Copy, AlertCircle } from 'lucide-react';

export default function WhiteLabelPage() {
  const { isIndependent } = useLicenseAccess();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [slugInput, setSlugInput] = useState('');
  const [slugCheck, setSlugCheck] = useState(null);
  const [checking, setChecking] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingHero, setUploadingHero] = useState(false);

  const load = useCallback(() => {
    if (isIndependent) return;
    api.get('/white-label/config')
      .then(r => {
        setConfig(r.data);
        setSlugInput(r.data.slug || r.data.slug_suggestion || '');
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [isIndependent]);

  useEffect(() => { load(); }, [load]);

  if (isIndependent) return <Navigate to="/dashboard" replace />;

  const checkSlug = async () => {
    if (!slugInput.trim()) return;
    setChecking(true);
    try {
      const r = await api.post('/white-label/slug/check', { slug: slugInput.trim() });
      setSlugCheck(r.data);
    } catch { setSlugCheck({ available: false, reason: 'Error al verificar' }); }
    finally { setChecking(false); }
  };

  const saveSlug = async () => {
    try {
      await api.post('/white-label/slug', { slug: slugInput.trim() });
      toast.success('Slug guardado');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const payload = {
        logo_url: config.logo_url || '',
        primary_color: config.primary_color || '',
        secondary_color: config.secondary_color || '',
        tertiary_color: config.tertiary_color || '',
        contact_email: config.contact_email || '',
        contact_phone: config.contact_phone || '',
        address: config.address || '',
        website: config.website || '',
        description: config.description || '',
        landing_headline: config.landing_headline || '',
        landing_description: config.landing_description || '',
        landing_hero_image: config.landing_hero_image || '',
        landing_cta_text: config.landing_cta_text || '',
        custom_domain: config.custom_domain || '',
      };
      await api.put('/white-label/config', payload);
      toast.success('Configuracion guardada');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  const toggleEnabled = async () => {
    try {
      if (!config.landing_enabled) {
        if (!config.slug) { toast.error('Primero configura tu slug'); return; }
        await api.post('/white-label/enable');
        toast.success('Landing activado');
      } else {
        await api.post('/white-label/disable');
        toast.success('Landing desactivado');
      }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const uploadImage = async (e, kind) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const setter = kind === 'logo' ? setUploadingLogo : setUploadingHero;
    setter(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await api.post('/upload/image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const url = r.data.url;
      setConfig(c => ({ ...c, [kind === 'logo' ? 'logo_url' : 'landing_hero_image']: url }));
      toast.success('Imagen subida. Guarda para aplicar.');
    } catch { toast.error('Error al subir'); }
    finally { setter(false); e.target.value = ''; }
  };

  const copyPublicUrl = () => {
    if (!config?.public_url) return;
    navigator.clipboard.writeText(config.public_url);
    toast.success('URL copiada');
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  const isDev = config?.business_type === 'developer';

  return (
    <div data-testid="white-label-page">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
        <div>
          <h2 className="font-heading text-2xl font-bold text-white flex items-center gap-2">
            <Globe className="w-6 h-6 text-primary" /> White Label
          </h2>
          <p className="text-muted-foreground text-sm mt-1">Personaliza el landing publico de tu empresa y activa tu subdominio.</p>
        </div>
        {config?.landing_enabled && <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20" data-testid="wl-status-active">Activo</Badge>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Principal */}
        <div className="lg:col-span-2 space-y-4">

          {/* Slug + enable */}
          <Card className="bg-card/50 backdrop-blur-md border-white/5" data-testid="wl-slug-card">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-white">Subdominio / Slug publico</p>
                  <p className="text-[11px] text-muted-foreground">Tu URL sera: {isDev ? '/developer/' : '/l/'}<strong>{config?.slug || 'slug'}</strong></p>
                </div>
                <Switch
                  checked={!!config?.landing_enabled}
                  onCheckedChange={toggleEnabled}
                  disabled={!config?.slug}
                  data-testid="wl-enable-switch"
                />
              </div>
              <div className="flex gap-2">
                <Input
                  value={slugInput}
                  onChange={e => { setSlugInput(e.target.value.toLowerCase()); setSlugCheck(null); }}
                  onBlur={checkSlug}
                  placeholder="mi-empresa"
                  className="bg-black/20 border-white/10 focus:border-primary"
                  data-testid="wl-slug-input"
                />
                <Button onClick={checkSlug} disabled={checking || !slugInput} variant="outline" className="border-white/10 text-white/80" data-testid="wl-slug-check-btn">
                  {checking ? <Loader2 className="w-4 h-4" /> : 'Verificar'}
                </Button>
                {slugCheck?.available && slugInput !== config?.slug && (
                  <Button onClick={saveSlug} className="bg-primary hover:bg-primary/90 text-black font-bold" data-testid="wl-slug-save-btn">
                    <Check className="w-4 h-4 mr-1" /> Guardar
                  </Button>
                )}
              </div>
              {slugCheck && (
                <div className={`flex items-center gap-2 text-xs ${slugCheck.available ? 'text-emerald-400' : 'text-amber-400'}`} data-testid="wl-slug-check-result">
                  {slugCheck.available ? <Check className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                  {slugCheck.available ? `"${slugCheck.slug}" esta disponible` : slugCheck.reason}
                </div>
              )}
              {config?.public_url && (
                <div className="p-3 rounded-lg bg-primary/5 border border-primary/20 flex items-center justify-between gap-2" data-testid="wl-public-url-block">
                  <code className="text-[11px] text-primary truncate flex-1">{config.public_url}</code>
                  <Button variant="ghost" size="sm" onClick={copyPublicUrl} className="h-7 text-primary hover:bg-primary/10" data-testid="wl-copy-url-btn">
                    <Copy className="w-3 h-3 mr-1" /> Copiar
                  </Button>
                  <a href={config.public_url} target="_blank" rel="noreferrer" className="text-primary" data-testid="wl-open-preview">
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Branding */}
          <Card className="bg-card/50 backdrop-blur-md border-white/5" data-testid="wl-branding-card">
            <CardContent className="p-6 space-y-4">
              <h3 className="text-sm font-semibold text-white">Branding</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Logo</Label>
                  <div className="flex gap-2 items-start">
                    {config?.logo_url && (
                      <div className="w-14 h-14 rounded-lg bg-black/20 border border-white/10 flex items-center justify-center overflow-hidden" style={{ backgroundImage: 'linear-gradient(45deg,#2a2a2a 25%,transparent 25%),linear-gradient(-45deg,#2a2a2a 25%,transparent 25%)', backgroundSize: '10px 10px' }}>
                        <img src={config.logo_url.startsWith('http') ? config.logo_url : `${process.env.REACT_APP_BACKEND_URL}${config.logo_url}`} alt="" className="w-full h-full object-contain" data-testid="wl-logo-preview" />
                      </div>
                    )}
                    <label className="cursor-pointer flex-1">
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e, 'logo')} disabled={uploadingLogo} data-testid="wl-logo-input" />
                      <Button variant="outline" className="border-white/10 w-full" disabled={uploadingLogo} asChild>
                        <span>{uploadingLogo ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ImageIcon className="w-4 h-4 mr-2" />} {config?.logo_url ? 'Cambiar' : 'Subir'} logo</span>
                      </Button>
                    </label>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Hero / Portada</Label>
                  <div className="flex gap-2 items-start">
                    {config?.landing_hero_image && (
                      <div className="w-14 h-14 rounded-lg bg-black/20 border border-white/10 overflow-hidden">
                        <img src={config.landing_hero_image.startsWith('http') ? config.landing_hero_image : `${process.env.REACT_APP_BACKEND_URL}${config.landing_hero_image}`} alt="" className="w-full h-full object-cover" />
                      </div>
                    )}
                    <label className="cursor-pointer flex-1">
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e, 'hero')} disabled={uploadingHero} data-testid="wl-hero-input" />
                      <Button variant="outline" className="border-white/10 w-full" disabled={uploadingHero} asChild>
                        <span>{uploadingHero ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ImageIcon className="w-4 h-4 mr-2" />} {config?.landing_hero_image ? 'Cambiar' : 'Subir'} portada</span>
                      </Button>
                    </label>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2">
                  <Label>Color primario</Label>
                  <div className="flex gap-2">
                    <input type="color" value={config?.primary_color || '#A5D62C'} onChange={e => setConfig(c => ({ ...c, primary_color: e.target.value }))} className="w-12 h-9 rounded border border-white/10 bg-transparent" data-testid="wl-color-primary" />
                    <Input value={config?.primary_color || ''} onChange={e => setConfig(c => ({ ...c, primary_color: e.target.value }))} placeholder="#A5D62C" className="bg-black/20 border-white/10 text-xs" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Secundario</Label>
                  <div className="flex gap-2">
                    <input type="color" value={config?.secondary_color || '#1B5E7B'} onChange={e => setConfig(c => ({ ...c, secondary_color: e.target.value }))} className="w-12 h-9 rounded border border-white/10 bg-transparent" data-testid="wl-color-secondary" />
                    <Input value={config?.secondary_color || ''} onChange={e => setConfig(c => ({ ...c, secondary_color: e.target.value }))} placeholder="#1B5E7B" className="bg-black/20 border-white/10 text-xs" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Terciario</Label>
                  <div className="flex gap-2">
                    <input type="color" value={config?.tertiary_color || '#0a0c12'} onChange={e => setConfig(c => ({ ...c, tertiary_color: e.target.value }))} className="w-12 h-9 rounded border border-white/10 bg-transparent" data-testid="wl-color-tertiary" />
                    <Input value={config?.tertiary_color || ''} onChange={e => setConfig(c => ({ ...c, tertiary_color: e.target.value }))} placeholder="#0a0c12" className="bg-black/20 border-white/10 text-xs" />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Landing content */}
          <Card className="bg-card/50 backdrop-blur-md border-white/5" data-testid="wl-content-card">
            <CardContent className="p-6 space-y-4">
              <h3 className="text-sm font-semibold text-white">Contenido del landing</h3>
              <div className="space-y-2">
                <Label>Headline principal</Label>
                <Input value={config?.landing_headline || ''} onChange={e => setConfig(c => ({ ...c, landing_headline: e.target.value }))} placeholder="Tu futuro comienza aqui" className="bg-black/20 border-white/10 focus:border-primary" data-testid="wl-headline-input" />
              </div>
              <div className="space-y-2">
                <Label>Descripcion larga</Label>
                <Textarea value={config?.landing_description || ''} onChange={e => setConfig(c => ({ ...c, landing_description: e.target.value }))} placeholder="Somos una empresa especializada en..." rows={4} className="bg-black/20 border-white/10 focus:border-primary" data-testid="wl-description-input" />
              </div>
              <div className="space-y-2">
                <Label>Texto del CTA</Label>
                <Input value={config?.landing_cta_text || ''} onChange={e => setConfig(c => ({ ...c, landing_cta_text: e.target.value }))} placeholder="Agenda tu visita" className="bg-black/20 border-white/10 focus:border-primary" data-testid="wl-cta-input" />
              </div>
            </CardContent>
          </Card>

          {/* Contact */}
          <Card className="bg-card/50 backdrop-blur-md border-white/5" data-testid="wl-contact-card">
            <CardContent className="p-6 space-y-4">
              <h3 className="text-sm font-semibold text-white">Datos de contacto publicos</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input value={config?.contact_email || ''} onChange={e => setConfig(c => ({ ...c, contact_email: e.target.value }))} className="bg-black/20 border-white/10 focus:border-primary" />
                </div>
                <div className="space-y-2">
                  <Label>Telefono</Label>
                  <Input value={config?.contact_phone || ''} onChange={e => setConfig(c => ({ ...c, contact_phone: e.target.value }))} className="bg-black/20 border-white/10 focus:border-primary" />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Direccion</Label>
                  <Input value={config?.address || ''} onChange={e => setConfig(c => ({ ...c, address: e.target.value }))} className="bg-black/20 border-white/10 focus:border-primary" />
                </div>
                <div className="space-y-2">
                  <Label>Sitio web</Label>
                  <Input value={config?.website || ''} onChange={e => setConfig(c => ({ ...c, website: e.target.value }))} placeholder="https://" className="bg-black/20 border-white/10 focus:border-primary" />
                </div>
                <div className="space-y-2">
                  <Label>Dominio personalizado (CNAME)</Label>
                  <Input value={config?.custom_domain || ''} onChange={e => setConfig(c => ({ ...c, custom_domain: e.target.value }))} placeholder="www.tu-empresa.com" className="bg-black/20 border-white/10 focus:border-primary" data-testid="wl-custom-domain-input" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Button onClick={saveConfig} disabled={saving} className="w-full bg-primary hover:bg-primary/90 text-black font-bold" data-testid="wl-save-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
            Guardar todos los cambios
          </Button>
        </div>

        {/* Preview sidebar */}
        <div className="space-y-4">
          <Card className="bg-card/50 backdrop-blur-md border-white/5 sticky top-4" data-testid="wl-preview-card">
            <CardContent className="p-4">
              <h3 className="text-xs font-semibold text-white uppercase tracking-wider mb-3">Vista previa</h3>
              {!config?.landing_enabled ? (
                <div className="p-4 text-center text-xs text-muted-foreground">
                  <X className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  Landing desactivado
                </div>
              ) : (
                <div className="space-y-3">
                  <div
                    className="p-4 rounded-lg text-xs space-y-2 border border-white/10"
                    style={{ backgroundColor: config.tertiary_color || '#0a0c12', color: '#fff' }}
                  >
                    {config.logo_url && (
                      <img src={config.logo_url.startsWith('http') ? config.logo_url : `${process.env.REACT_APP_BACKEND_URL}${config.logo_url}`} alt="" className="h-8" />
                    )}
                    <p className="text-sm font-bold" style={{ color: config.primary_color }}>{config.landing_headline || config.name}</p>
                    <p className="text-[10px] opacity-80 leading-relaxed">{(config.landing_description || '').slice(0, 180)}{config.landing_description?.length > 180 ? '...' : ''}</p>
                    <button
                      className="text-[10px] px-3 py-1.5 rounded-full font-bold"
                      style={{ backgroundColor: config.primary_color, color: '#000' }}
                    >
                      {config.landing_cta_text || 'Contactar'}
                    </button>
                  </div>
                  {config.public_url && (
                    <a href={config.public_url} target="_blank" rel="noreferrer" className="block text-center text-[11px] text-primary hover:underline" data-testid="wl-open-landing">
                      <ExternalLink className="w-3 h-3 inline mr-1" /> Abrir landing real
                    </a>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
